import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, map, of, tap, type Observable } from 'rxjs';

import {
  NotificationCategory,
  NotificationsApiService,
  type NotificationDto,
  type NotificationPreferenceDto,
  type NotificationPreferenceDtoListApiResponse,
  type StringApiResponse,
} from 'api';
import { ToastService } from '@core/services/toast.service';
import { getErrorMessage } from '@shared/utils';

const PAGE_SIZE = 20;

/**
 * Estado único (signals) de las notificaciones in-app del usuario. Solo orquesta REST y mantiene
 * el estado; la conexión de tiempo real vive en `NotificationRealtimeService`, que llama a
 * `pushRealtime`/`setUnreadCount` sobre este store.
 *
 * Las acciones de mutación (marcar leída, marcar todas, eliminar) son **optimistas**: actualizan
 * el signal de inmediato y revierten si el backend falla, mostrando un toast.
 */
@Injectable({ providedIn: 'root' })
export class NotificationStoreService {
  private readonly api = inject(NotificationsApiService);
  private readonly toast = inject(ToastService);

  private readonly _items = signal<NotificationDto[]>([]);
  private readonly _unreadCount = signal(0);
  private readonly _loading = signal(false);
  private readonly _loadingMore = signal(false);
  private readonly _hasNextPage = signal(false);
  private readonly _currentPage = signal(0);
  private readonly _categoryFilter = signal<NotificationCategory | null>(null);

  /** Notificaciones cargadas, de la más reciente a la más antigua. */
  readonly items = this._items.asReadonly();
  /** Contador de no leídas para el badge (fuente de verdad: backend). */
  readonly unreadCount = this._unreadCount.asReadonly();
  /** Cargando la primera página. */
  readonly loading = this._loading.asReadonly();
  /** Cargando una página adicional (paginación). */
  readonly loadingMore = this._loadingMore.asReadonly();
  /** Hay más páginas por cargar. */
  readonly hasNextPage = this._hasNextPage.asReadonly();
  /** Filtro de categoría activo (null = todas). */
  readonly categoryFilter = this._categoryFilter.asReadonly();
  /** Hay al menos una notificación cargada. */
  readonly hasItems = computed(() => this._items().length > 0);

  /** Carga (o recarga) la primera página respetando el filtro de categoría activo. */
  loadFirstPage(): void {
    this._loading.set(true);
    this.fetchPage(1).subscribe((items) => {
      this._items.set(items);
      this._loading.set(false);
    });
  }

  /** Carga la página siguiente y la concatena al final. No hace nada si no hay más o ya carga. */
  loadMore(): void {
    if (!this._hasNextPage() || this._loadingMore() || this._loading()) return;
    this._loadingMore.set(true);
    this.fetchPage(this._currentPage() + 1).subscribe((items) => {
      this._items.update((current) => [...current, ...items]);
      this._loadingMore.set(false);
    });
  }

  /** Re-consulta el contador de no leídas. */
  refreshUnreadCount(): void {
    this.api
      .apiV1NotificationsUnreadCountGet()
      .pipe(catchError(() => of(null)))
      .subscribe((resp) => {
        if (resp && typeof resp.data === 'number') this._unreadCount.set(resp.data);
      });
  }

  /** Cambia el filtro de categoría y recarga la primera página. */
  setCategoryFilter(category: NotificationCategory | null): void {
    this._categoryFilter.set(category);
    this.loadFirstPage();
  }

  /** Marca una notificación como leída (optimista). Idempotente en backend. */
  markRead(id: string): void {
    const target = this._items().find((n) => n.id === id);
    if (!target || target.isRead) return;

    const snapshot = this.snapshot();
    this.applyRead(id);
    this.decrementUnread();

    this.api
      .apiV1NotificationsIdReadPost({ id })
      .pipe(catchError((error: unknown) => this.revert(snapshot, error)))
      .subscribe();
  }

  /** Marca todas como leídas (optimista). */
  markAllRead(): void {
    if (this._unreadCount() === 0 && this._items().every((n) => n.isRead)) return;

    const snapshot = this.snapshot();
    this._items.update((items) => items.map((n) => (n.isRead ? n : { ...n, isRead: true, readAt: null })));
    this._unreadCount.set(0);

    this.api
      .apiV1NotificationsReadAllPost()
      .pipe(catchError((error: unknown) => this.revert(snapshot, error)))
      .subscribe();
  }

  /** Elimina una notificación para el usuario (optimista, soft-delete en backend). */
  remove(id: string): void {
    const target = this._items().find((n) => n.id === id);
    if (!target) return;

    const snapshot = this.snapshot();
    this._items.update((items) => items.filter((n) => n.id !== id));
    if (!target.isRead) this.decrementUnread();

    this.api
      .apiV1NotificationsIdDelete({ id })
      .pipe(catchError((error: unknown) => this.revert(snapshot, error)))
      .subscribe();
  }

  /** Inserta una notificación recibida en tiempo real (al inicio, sin duplicar por id). */
  pushRealtime(notification: NotificationDto): void {
    if (!notification.id) return;
    this._items.update((items) => {
      if (items.some((n) => n.id === notification.id)) return items;
      return [notification, ...items];
    });
  }

  /** Setea el contador de no leídas (evento `UnreadCountChanged` del hub). */
  setUnreadCount(count: number): void {
    this._unreadCount.set(Math.max(0, count));
  }

  /** Limpia todo el estado (logout). */
  reset(): void {
    this._items.set([]);
    this._unreadCount.set(0);
    this._loading.set(false);
    this._loadingMore.set(false);
    this._hasNextPage.set(false);
    this._currentPage.set(0);
    this._categoryFilter.set(null);
  }

  // ===========================================================================
  // PREFERENCIAS DE NOTIFICACIÓN (consultas independientes del listado)
  // ===========================================================================

  /** Preferencias de notificación del usuario (matriz categoría × canal). */
  getPreferences(): Observable<NotificationPreferenceDtoListApiResponse> {
    return this.api.apiV1NotificationsPreferencesGet();
  }

  /** Guarda (upsert) las preferencias de notificación del usuario. */
  updatePreferences(preferences: NotificationPreferenceDto[]): Observable<StringApiResponse> {
    return this.api.apiV1NotificationsPreferencesPut({
      updateNotificationPreferencesDto: { preferences },
    });
  }

  private fetchPage(page: number) {
    return this.api
      .apiV1NotificationsGet({
        page,
        pageSize: PAGE_SIZE,
        category: this._categoryFilter() ?? undefined,
      })
      .pipe(
        tap((resp) => {
          const pagination = resp.data?.pagination;
          this._currentPage.set(pagination?.currentPage ?? page);
          this._hasNextPage.set(pagination?.hasNextPage ?? false);
        }),
        map((resp) => resp.data?.data ?? []),
        catchError((error: unknown) => {
          this.toast.error(getErrorMessage(error) ?? 'No se pudieron cargar las notificaciones');
          this._hasNextPage.set(false);
          return of([] as NotificationDto[]);
        })
      );
  }

  private applyRead(id: string): void {
    this._items.update((items) => items.map((n) => (n.id === id ? { ...n, isRead: true, readAt: null } : n)));
  }

  private decrementUnread(): void {
    this._unreadCount.update((c) => Math.max(0, c - 1));
  }

  private snapshot(): { items: NotificationDto[]; unread: number } {
    return { items: this._items(), unread: this._unreadCount() };
  }

  private revert(snapshot: { items: NotificationDto[]; unread: number }, error: unknown) {
    this._items.set(snapshot.items);
    this._unreadCount.set(snapshot.unread);
    this.toast.error(getErrorMessage(error) ?? 'No se pudo completar la acción sobre la notificación');
    return of(null);
  }
}
